import {
  Megaphone,
  Users,
  Building2,
  BarChart3,
  Handshake,
  Send,
  Settings,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link, matchPath, useLocation } from "react-router";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";

// Idem CRM app shell — the persistent left-panel sidebar (Wave 1, slice 0).
// Exposes the product's 7 modules as the navigation IA (docs/15 §3). This is the
// nav shell only: built modules point at existing screens; not-yet-built modules
// route to a clean placeholder; Settings lands on its tiled hub (§4).

type NavChild = { label: string; to: string; match: string[] };
type NavModule = {
  label: string;
  icon: LucideIcon;
  to: string;
  match: string[];
  children?: NavChild[];
};

// Nav order top→bottom = the module architecture (docs/15). "to" is where the
// module's parent link lands; "match" drives active-state (incl. deep routes).
const MODULES: NavModule[] = [
  {
    label: "Marketing",
    icon: Megaphone,
    to: "/work",
    match: ["/", "/work", "/drafts", "/dialer", "/voicemails", "/list-stacking", "/campaigns", "/reports-me"],
    children: [
      { label: "Today's Work", to: "/work", match: ["/", "/work"] },
      { label: "List Stacking", to: "/list-stacking", match: ["/list-stacking"] },
      { label: "Campaigns", to: "/campaigns", match: ["/campaigns"] },
      { label: "Dialer", to: "/dialer", match: ["/dialer"] },
      { label: "Reports", to: "/reports-me", match: ["/reports-me"] },
      { label: "Voicemails", to: "/voicemails", match: ["/voicemails"] },
      { label: "Drafts", to: "/drafts", match: ["/drafts"] },
    ],
  },
  {
    label: "Leads",
    icon: Users,
    to: "/leads",
    match: ["/leads", "/leads/*", "/deals", "/deals/*"],
    children: [
      { label: "Leads", to: "/leads", match: ["/leads", "/leads/*"] },
      { label: "Deals / Acquisitions", to: "/deals", match: ["/deals", "/deals/*"] },
    ],
  },
  { label: "MLS", icon: Building2, to: "/mls", match: ["/mls"] },
  {
    label: "Reports",
    icon: BarChart3,
    to: "/kpi",
    match: ["/kpi", "/reports"],
    children: [{ label: "KPI Dashboard", to: "/kpi", match: ["/kpi"] }],
  },
  { label: "Buyers", icon: Handshake, to: "/buyers", match: ["/buyers"] },
  { label: "Dispositions", icon: Send, to: "/dispositions", match: ["/dispositions"] },
  { label: "Settings", icon: Settings, to: "/settings", match: ["/settings", "/config"] },
];

const ModuleSidebar = () => {
  const { pathname } = useLocation();
  const { openMobile, setOpenMobile } = useSidebar();
  const isActive = (patterns: string[]) => patterns.some((p) => !!matchPath(p, pathname));
  const closeOnMobile = () => openMobile && setOpenMobile(false);

  return (
    <Sidebar variant="floating" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:!p-1.5">
              <Link to="/" onClick={closeOnMobile}>
                <Building2 className="!size-5" />
                <span className="text-base font-semibold">Idem CRM</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {MODULES.map((m) => {
                const active = isActive(m.match);
                return (
                  <SidebarMenuItem key={m.label}>
                    <SidebarMenuButton asChild isActive={active} tooltip={m.label}>
                      <Link to={m.to} onClick={closeOnMobile}>
                        <m.icon />
                        <span>{m.label}</span>
                      </Link>
                    </SidebarMenuButton>
                    {m.children && active && (
                      <SidebarMenuSub>
                        {m.children.map((c) => (
                          <SidebarMenuSubItem key={c.label}>
                            <SidebarMenuSubButton asChild isActive={isActive(c.match)}>
                              <Link to={c.to} onClick={closeOnMobile}>
                                <span>{c.label}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

export default ModuleSidebar;
